import { useEffect, useMemo } from 'react';
import { compareWithAppVersion } from '@croquiscom/web2app';
import { vars } from '@croquiscom-pvt/zds/dist/tokens';
import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { overwriteOnMessage, ZigzagEvent, ZpayPostMessageType } from '@common/app-manager';
import { ZigzagEvent as ZigzagLogEvent } from '@common/log-manager';
import { ReveiwListConfigValue, SummaryCardServiceProps } from '@common/review';
import { web_max_width } from '@common/styles';
import { Notify, NotifyIcon } from '@common/toolbox';
import {
  addZzSessionData,
  CSPHead,
  ErrorBoundaryWrapper,
  getZzSessionData,
  isPreview,
  PdpCatalogSalesStatus,
  PdpPageviewEventLog,
  PdpTtiLog,
  PreloadHead,
  TabType,
  useCatalogBrowsingTypeQuery,
  useCatalogProductIdParams,
  useIsAuthenticated,
  usePdpBaseInfo,
} from '@domains/pdp-common';
import {
  BeautySummaryContainer,
  CategoryContainer,
  CouponBannerContainer,
  CreditRewardsContainer,
  CrossSellerProductContainer,
  EpikStylistContainer,
  FastShippingBannerContainer,
  FirstOrderEnableTextContainer,
  FirstPurchaseDealsContainer,
  GaugebarCouponNotApplicableTextContainer,
  MaximumBenefitContainer,
  MileageUsableInfoContainer,
  PaymentContainer,
  RelatedItemContainer,
  ReviewSummaryContainer,
  RollingBannerContainer,
  SellerInfoContainer,
  ShippingInfoContainer,
  ShippingNoticeBannerContainer,
  ShopContainer,
  StoreDealBannerContainer,
  TabsContainer,
  TopRecommendListContainer,
  useCouponAutoDownloadFromNaverEp,
  WelcomeBannerContainer,
  ZigzinBannerContainer,
} from '@domains/pdp-features';

import { LegacyATFScreen } from './LegacyATFScreen';

const CreatorLoungePdpCouponsBottomSheet = dynamic(
  () => import('@widgets/ad').then(({ CreatorLoungePdpCouponsBottomSheet }) => CreatorLoungePdpCouponsBottomSheet),
  { ssr: false },
);

interface Props {
  featureList: string[];
  ReviewSummaryComponent: (props: ReveiwListConfigValue & SummaryCardServiceProps) => JSX.Element;
  tabComponentList: Record<TabType, JSX.Element>;
}

const SHOP_COUNT_SESSION_DATA_KEY = 'shop_count';
const PDP_VISIBLE_COUNT = 'visible_count';

export function ProductDetailScreen({ featureList, ReviewSummaryComponent, tabComponentList }: Props) {
  const country = 'KR';
  const { asPath, query } = useRouter();
  const _isPreview = isPreview(asPath);

  const { isLoggedIn } = useIsAuthenticated();
  const { isNative } = useCatalogBrowsingTypeQuery();
  const param = useCatalogProductIdParams();
  const {
    shop,
    product,
    key_color: { discount_info_of_atf },
  } = usePdpBaseInfo(param);

  const pageViewEventInfo = {
    params: {
      product_id: product.id,
      product_name: product.name,
      price: product.product_price.final_discount_info.discount_price,
      quantity: 1,
      category: product.category_list?.[0]?.value ?? '',
      shop_id: shop.id,
      managedCategoryList: product.managed_category_list,
      shop_name: shop.name,
    },
    enabledAirbridge: !isNative,
  };
  const isKorea = country === 'KR';

  const updateShopCountSessionData = () => {
    // BRAZE LOG: 동 세션에서 열람한 상품의 카운트와 처음 접근한 상품의 쇼핑몰에서 판매하는 상품을 몇회 이상 확인했는지 데이터 누적
    try {
      const shopCountSessionData: Record<string, number> = getZzSessionData(SHOP_COUNT_SESSION_DATA_KEY);
      const visibleCountSessionData: Record<string, number> = getZzSessionData(PDP_VISIBLE_COUNT);
      const shopName = Object.keys(shopCountSessionData).length > 0 ? Object.keys(shopCountSessionData)[0] : null;

      if (!shopName) {
        shopCountSessionData[shop.name] = 1;
      } else if (shopName === shop.name) {
        shopCountSessionData[shopName] = Number(shopCountSessionData[shopName]) + 1;
      }

      visibleCountSessionData.count = visibleCountSessionData.count ? Number(visibleCountSessionData.count) + 1 : 1;

      addZzSessionData({ name: SHOP_COUNT_SESSION_DATA_KEY, value: shopCountSessionData });
      addZzSessionData({ name: PDP_VISIBLE_COUNT, value: visibleCountSessionData });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!product.id) {
      return;
    }

    if (compareWithAppVersion('7.19.0')) {
      updateShopCountSessionData();

      const shopCountSessionData = getZzSessionData(SHOP_COUNT_SESSION_DATA_KEY);
      const visibleCountSessionData = getZzSessionData(PDP_VISIBLE_COUNT);
      const shopName = Object.keys(shopCountSessionData)[0];

      const brazeLog = {
        product_id: product.id,
        shop_id: shop.id,
        shop_name: shop.name,
        shop_domain_name: shop.main_domain,
        product_name: product.name,
        price: product.product_price.final_discount_info.discount_price,
        product_category_lv1: product.category_list?.[0]?.value,
        pdp_count: visibleCountSessionData.count,
        source: query.zz_source,
        campaign: query.zz_campaign,
      };

      // BRAZE LOG: 동 세션에서 처음 접근한 상품의 쇼핑몰에서 판매하는 상품을 3회 이상 봤을 경우 PDP 접근 카운트와 함께 발송
      const shopVisibleLog =
        Number(shopCountSessionData[shopName]) >= 3
          ? {
              pdp_count_store: shopCountSessionData[shopName],
              pdp_count_store_name: shopName,
            }
          : {};

      ZigzagLogEvent.logBraze('view_product', {
        ...brazeLog,
        ...shopVisibleLog,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!product.id) {
      return;
    }

    overwriteOnMessage((type) => {
      switch (type) {
        case ZpayPostMessageType.FORCE_REFRESH_PAGE: {
          window.location.reload();
          break;
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // 1x1 웹뷰 트래픽용 페이지 생성을 위한 인터페이스 호출
  useEffect(() => {
    if (!product.id || !product.tracking_product_url) {
      return;
    }

    ZigzagEvent.sel(product.tracking_product_url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errorMessage = useMemo(() => {
    if (!product.id) {
      return '요청에 실패했습니다';
    }
    if (!param.catalog_product_id || product.sales_status === PdpCatalogSalesStatus.CLOSED) {
      return '존재하지 않는 상품입니다';
    }
    if (param.catalog_product_id !== product.id) {
      return '잘못된 응답입니다.';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, shop, param.catalog_product_id]);

  useCouponAutoDownloadFromNaverEp({
    shop_id: shop.id,
    product_id: product.id,
    fulfillment_type: product.fulfillment_type,
    is_logged_in: isLoggedIn,
  });

  if (errorMessage) {
    if (isNative) {
      return (
        <Notify
          title={'서비스 이용이 원활하지 않아요'}
          icon_type={NotifyIcon.ERROR}
          contents={['불편을 드려 죄송해요.', '잠시 후에 다시 시도해 주세요.']}
          onConfirm={() => location.reload()}
        />
      );
    }

    return <Notify title={errorMessage} icon_type={NotifyIcon.WARNING} />;
  }

  return (
    <PdpPageviewEventLog disableLogging={_isPreview} pageViewEventInfo={pageViewEventInfo}>
      <Screen discountColor={discount_info_of_atf}>
        <CSPHead />
        {!isNative && (
          <>
            <PreloadHead />
            <PdpTtiLog productId={product.id}>
              <LegacyATFScreen featureList={featureList} />
            </PdpTtiLog>
          </>
        )}

        <ErrorBoundaryWrapper>
          <StoreDealBannerContainer />
        </ErrorBoundaryWrapper>

        <ErrorBoundaryWrapper>
          <FirstOrderEnableTextContainer />
        </ErrorBoundaryWrapper>

        <ErrorBoundaryWrapper>
          <WelcomeBannerContainer />
        </ErrorBoundaryWrapper>

        <ErrorBoundaryWrapper>
          <GaugebarCouponNotApplicableTextContainer />
        </ErrorBoundaryWrapper>

        <ErrorBoundaryWrapper>
          <MaximumBenefitContainer />
        </ErrorBoundaryWrapper>

        <div className='base_info_wrapper'>
          <ErrorBoundaryWrapper>
            <MileageUsableInfoContainer />
          </ErrorBoundaryWrapper>

          <ErrorBoundaryWrapper>
            <CreditRewardsContainer />
          </ErrorBoundaryWrapper>

          <ErrorBoundaryWrapper>
            <PaymentContainer />
          </ErrorBoundaryWrapper>

          <ErrorBoundaryWrapper>
            <ShippingInfoContainer />
          </ErrorBoundaryWrapper>

          <ErrorBoundaryWrapper>
            <ShippingNoticeBannerContainer />
          </ErrorBoundaryWrapper>
        </div>

        <ErrorBoundaryWrapper>
          <CouponBannerContainer />
        </ErrorBoundaryWrapper>

        <ErrorBoundaryWrapper>
          <FastShippingBannerContainer />
        </ErrorBoundaryWrapper>

        <ErrorBoundaryWrapper>
          <ZigzinBannerContainer />
        </ErrorBoundaryWrapper>

        <ErrorBoundaryWrapper>
          <RollingBannerContainer />
        </ErrorBoundaryWrapper>

        <ErrorBoundaryWrapper>
          <ReviewSummaryContainer Component={ReviewSummaryComponent} />
        </ErrorBoundaryWrapper>

        <ErrorBoundaryWrapper>
          <CrossSellerProductContainer />
        </ErrorBoundaryWrapper>

        {isKorea && (
          <>
            <ErrorBoundaryWrapper>
              <FirstPurchaseDealsContainer />
            </ErrorBoundaryWrapper>

            <ErrorBoundaryWrapper>
              <BorderBottomNone>
                <TopRecommendListContainer productId={param.catalog_product_id} />
              </BorderBottomNone>
            </ErrorBoundaryWrapper>
          </>
        )}

        <ErrorBoundaryWrapper>
          <EpikStylistContainer className='divider_top' />
        </ErrorBoundaryWrapper>

        <ErrorBoundaryWrapper>
          <RelatedItemContainer relationType='SAMPLE_ORIGINAL' />
        </ErrorBoundaryWrapper>

        <ErrorBoundaryWrapper>
          <div className='shop_row'>
            <ShopContainer className='pdp__shop' />
            <CategoryContainer />
            <SellerInfoContainer />
          </div>
        </ErrorBoundaryWrapper>

        <ErrorBoundaryWrapper>
          <BeautySummaryContainer />
        </ErrorBoundaryWrapper>

        <ErrorBoundaryWrapper>
          <TabsContainer tabComponentList={tabComponentList} />
        </ErrorBoundaryWrapper>
        <ErrorBoundaryWrapper>
          <CreatorLoungePdpCouponsBottomSheet webview_type='product_detail' />
        </ErrorBoundaryWrapper>
      </Screen>
    </PdpPageviewEventLog>
  );
}

const Screen = styled.div<{ discountColor: string }>`
  ${({ discountColor }) =>
    discountColor &&
    `--zds-component-product-card-metadata-price-discount-percent-text-color: ${discountColor};`}; //MEMO: 상품카드 4.0

  background: #fff;
  max-width: ${web_max_width}px;
  min-height: 100vh;
  margin: 0 auto;
  padding: env(safe-area-inset-top) 0 calc(env(safe-area-inset-bottom));
  padding: constant(safe-area-inset-top) 0 calc(constant(safe-area-inset-bottom));

  & > nav {
    position: sticky;
    top: 0;
    width: 100%;
    max-width: ${web_max_width}px;
    z-index: 205;
  }

  .base_info_wrapper {
    border-top: 1px solid ${vars.color.dividerThin};

    & > div {
      border-bottom: 1px solid ${vars.color.dividerThin};

      &:nth-last-of-type(1) {
        border-bottom: none;
      }
    }
  }

  .pdp__bottomFloating {
    position: fixed;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    max-width: ${web_max_width}px;
    z-index: 205;
  }

  .pdp_shop_info_row {
    display: flex;
    justify-content: space-between;
    margin: 0 0 14px 0;
    border-bottom: 1px solid ${vars.color.gray10};
  }

  .pdp__title {
    padding: 0 16px;
  }

  .pdp__shop {
    border-top: 8px solid ${vars.color.gray10};
  }

  .title_row {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .shop_row {
    margin: 0;
  }

  .divider_top {
    border-top: 8px solid ${vars.color.gray10};
  }
`;

const BorderBottomNone = styled.div`
  section {
    border-bottom: none;
  }
`;
